/**
 * Nutrition Tracking Validation Schemas
 */

import { z } from 'zod';
import { NUTRITION_LIMITS } from './constants';

// Meal type enum
const mealTypeSchema = z.enum(['breakfast', 'lunch', 'dinner', 'snack', 'water']);

// Nutrition log form schema
export const nutritionLogSchema = z.object({
  meal_type: mealTypeSchema,
  food_description: z.string().max(500).optional().nullable(),
  calories: z
    .number()
    .int()
    .min(NUTRITION_LIMITS.calories.min)
    .max(NUTRITION_LIMITS.calories.max)
    .optional()
    .nullable(),
  protein_g: z
    .number()
    .min(NUTRITION_LIMITS.protein_g.min)
    .max(NUTRITION_LIMITS.protein_g.max)
    .optional()
    .nullable(),
  carbs_g: z
    .number()
    .min(NUTRITION_LIMITS.carbs_g.min)
    .max(NUTRITION_LIMITS.carbs_g.max)
    .optional()
    .nullable(),
  fat_g: z
    .number()
    .min(NUTRITION_LIMITS.fat_g.min)
    .max(NUTRITION_LIMITS.fat_g.max)
    .optional()
    .nullable(),
  fiber_g: z
    .number()
    .min(NUTRITION_LIMITS.fiber_g.min)
    .max(NUTRITION_LIMITS.fiber_g.max)
    .optional()
    .nullable(),
  water_oz: z
    .number()
    .int()
    .min(NUTRITION_LIMITS.water_oz.min)
    .max(NUTRITION_LIMITS.water_oz.max)
    .optional()
    .nullable(),
  client_notes: z.string().max(1000).optional().nullable(),
  logged_date: z.string().optional(),
}).refine(
  (data) => {
    // At least one nutritional value must be provided
    return (
      data.calories != null ||
      data.protein_g != null ||
      data.carbs_g != null ||
      data.fat_g != null ||
      data.fiber_g != null ||
      data.water_oz != null
    );
  },
  {
    message: 'At least one nutritional value is required',
    path: ['calories'],
  }
);

// Water-only quick log schema
export const waterLogSchema = z.object({
  water_oz: z
    .number()
    .int()
    .min(1)
    .max(NUTRITION_LIMITS.water_oz.max),
  logged_date: z.string().optional(),
});

// Nutrition goal schema
export const nutritionGoalSchema = z.object({
  target_calories: z
    .number()
    .int()
    .min(500)
    .max(NUTRITION_LIMITS.calories.max)
    .optional()
    .nullable(),
  target_protein_g: z
    .number()
    .int()
    .min(0)
    .max(NUTRITION_LIMITS.protein_g.max)
    .optional()
    .nullable(),
  target_carbs_g: z
    .number()
    .int()
    .min(0)
    .max(NUTRITION_LIMITS.carbs_g.max)
    .optional()
    .nullable(),
  target_fat_g: z
    .number()
    .int()
    .min(0)
    .max(NUTRITION_LIMITS.fat_g.max)
    .optional()
    .nullable(),
  target_fiber_g: z
    .number()
    .int()
    .min(0)
    .max(NUTRITION_LIMITS.fiber_g.max)
    .optional()
    .nullable(),
  target_water_oz: z
    .number()
    .int()
    .min(0)
    .max(NUTRITION_LIMITS.water_oz.max)
    .optional()
    .nullable(),
  effective_from: z.string().optional(),
  effective_until: z.string().optional().nullable(),
  notes: z.string().max(1000).optional().nullable(),
});

// Approval action schema
export const approvalSchema = z.object({
  action: z.enum(['approve', 'reject', 'request_edit']),
  trainer_notes: z.string().max(1000).optional(),
});

// Type exports
export type NutritionLogFormData = z.infer<typeof nutritionLogSchema>;
export type WaterLogFormData = z.infer<typeof waterLogSchema>;
export type NutritionGoalFormData = z.infer<typeof nutritionGoalSchema>;
export type ApprovalFormData = z.infer<typeof approvalSchema>;
